import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { IoEyeOutline, IoFilterOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { BsCalendar4 } from "react-icons/bs";
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { usePendingRequests } from '../../context/PendingRequestContext';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatDate } from '../../utils/format';
import { toast } from 'react-toastify';
import { HiOutlineClipboardList } from "react-icons/hi";
import { useRef } from 'react';
import { IoFilterSharp } from 'react-icons/io5';

// ─── Shared config ────────────────────────────────────────────────────────────



const statusConfig = {
  APPROVED: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", border: "border-emerald-200", label: "Approved" },
  PENDING:  { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-400",   border: "border-amber-200",   label: "Pending"  },
  REJECTED: { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     border: "border-red-200",     label: "Rejected" },
  CANCELLED:{ bg: "bg-slate-100",  text: "text-slate-500",   dot: "bg-slate-400",   border: "border-slate-200",   label: "Cancelled"},
};

const leaveTypeAbbr = {
  "Earned Leave": "EL", "Sick Leave": "SL", "Comp Off Leave": "CO",
  "Emergency Leave": "EM", "LOP Leave": "LOP",
};
const leaveTypeColor = {
  "Earned Leave": "bg-blue-100 text-blue-700",
  "Sick Leave": "bg-rose-100 text-rose-700",
  "Comp Off Leave": "bg-violet-100 text-violet-700",
  "Emergency Leave": "bg-orange-100 text-orange-700",
  "LOP Leave": "bg-slate-200 text-slate-600",
};

// ─── Avatar initials helper ───────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return 'NA'; // fallback for null, undefined, empty

  return name
    .split(' ')
    .map(n => n?.[0] || '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const avatarColors = [
  "bg-indigo-100 text-indigo-700", "bg-pink-100 text-pink-700",
  "bg-teal-100 text-teal-700",    "bg-amber-100 text-amber-700",
  "bg-cyan-100 text-cyan-700",
];
const avatarColor = (name) => {
  if (!name) return avatarColors[0];
  return avatarColors[name.charCodeAt(0) % avatarColors.length];
};

function ManagerLeaveRequests() {

  // Filter states
const [managers, setManagers] = useState([]);
const [selectedManager, setSelectedManager] = useState('');
const [selectedLocation, setSelectedLocation] = useState('');
const [leaveType, setLeaveType] = useState('ALL');
const [status, setStatus] = useState('ALL'); // separate from statusFilter

// Draft states (for filter panel)
const [draftManager, setDraftManager] = useState('');
const [draftLocation, setDraftLocation] = useState('');
const [draftType, setDraftType] = useState('ALL');
const [draftStatus, setDraftStatus] = useState('ALL');

const [filterOpen, setFilterOpen] = useState(false);
const filterRef = useRef(null);



  const navigate = useNavigate();
  const { currentUser, loading } = useUser();
  const { updatePendingCount } = usePendingRequests();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectComment, setRejectComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [apiLoading, setLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isReapproving, setIsReapproving] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  // const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  // const [bulkRejectComment, setBulkRejectComment] = useState('');
  // const [isBulkApproving, setIsBulkApproving] = useState(false);
  // const [isBulkRejecting, setIsBulkRejecting] = useState(false);
  const requestsPerPage = 6;

  // Role-based dynamic configuration
  const isCEO = currentUser?.role === "CEO";
  const roleKey = isCEO ? "ceoId" : "managerId";
  const pageTitle = isCEO ? "Company Leave Requests" : "Team Leave Requests";

const activeFilterCount = isCEO
  ? [
      leaveType !== 'ALL' ? leaveType : '',
      status !== 'ALL' ? status : '',
      selectedManager,
      selectedLocation
    ].filter(Boolean).length
  : [
      status !== 'ALL' ? status : '',
      selectedLocation
    ].filter(Boolean).length;

  const fetchLeaveRequests = async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      // Build params object based on role
      const params = {
        days: 62,
      };
      
      // Add status filter if not 'ALL'
      if (status !== 'ALL') {
        params.status = status;
      }
      
      // For CEO: add leaveType and location filters
      if (isCEO) {
        if (leaveType !== 'ALL') {
          params.leaveType = leaveType;
        }
        if (selectedLocation) {
          params.location = selectedLocation;
        }
      } else {
        // For Manager: only add location filter
        if (selectedLocation) {
          params.location = selectedLocation;
        }
      }

      const response = await api.get(`/api/leaves/recent?${roleKey}=${currentUser?.id}`, {
        params
      });
      
      const dataWithIds = response.data.map(req => ({
        id: req.leaveRequestId,
        employeeId: req.employeeId,
        employeeName: req.employeeName,
        startDate: req.leaveStartDate,
        endDate: req.leaveEndDate,
        startDateTime: req.startDateTime,
        endDateTime: req.endDateTime,
        type: req.leaveType,
        status: req.leaveStatus,
        reason: req.reason,
        duration: req.duration,
        approverComment: req.approverComment,
        appliedOn: req.appliedOn,
        lastModifiedOn: req.lastModifiedOn,
      }));
      setLeaveRequests(dataWithIds);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchManagers = async () => {
  try {
    const res = await api.get('/auth/managers');
    setManagers(res.data || []);
  } catch (e) {
    console.error('Failed to fetch managers', e);
  }
};

useEffect(() => {
  if (currentUser?.id) fetchLeaveRequests();
}, [leaveType, status, selectedManager, selectedLocation]);


useEffect(() => { fetchManagers(); }, []);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (filterRef.current && !filterRef.current.contains(e.target)) {
      setFilterOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);


  useEffect(() => { if (currentUser?.id) fetchLeaveRequests(); }, [currentUser?.id, status, leaveType, selectedLocation]);

  const filteredRequests = leaveRequests.filter(request => {
    // Filter by status - using leaveStatus from the DTO
    if (status !== 'ALL' && request.leaveStatus !== status) return false;
    
    // Filter by leave type (CEO only uses this)
    if (isCEO && leaveType !== 'ALL' && request.leaveType !== leaveType) return false;
    
    // Filter by location (if available in request)
    if (selectedLocation && request.location && request.location !== selectedLocation) return false;
    
    return true;
  });
  
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage, currentPage * requestsPerPage
  );

  const handleView = (request) => { setSelectedRequest(request); setShowViewModal(true); };

  const fetchPendingCount = async () => {
    try {
      const response = await api.get(`/api/leaves/pending-count?${roleKey}=${currentUser?.id}`);
      updatePendingCount(response.data);
    } catch (error) { console.error('Error fetching pending count:', error); }
  };

  const handleApprove = async (requestId, isReapproval = false) => {
    if (!currentUser?.id) return;
    isReapproval ? setIsReapproving(true) : setIsApproving(true);
    try {
      await api.put(`/api/leaves/approve/${requestId}`, null, { params: { [roleKey]: currentUser?.id } });
      await fetchPendingCount(); await fetchLeaveRequests();
      setShowViewModal(false);
      toast.success(`Leave request ${isReapproval ? 'reapproved' : 'approved'} successfully`);
    } catch (error) { toast.error(`${isReapproval ? 'Reapproval' : 'Approval'} failed`); }
    finally { isReapproval ? setIsReapproving(false) : setIsApproving(false); }
  };

  const handleReject = async (requestId) => {
    if (!currentUser?.id || !rejectComment.trim()) { toast.error('Please provide a reason for rejection'); return; }
    setIsRejecting(true);
    try {
      await api.put(`/api/leaves/reject/${requestId}`, null, { params: { [roleKey]: currentUser?.id, comment: rejectComment } });
      await fetchPendingCount(); await fetchLeaveRequests();
      setShowViewModal(false); setRejectComment('');
      toast.success('Leave request rejected successfully');
    } catch (error) { toast.error('Rejection failed'); }
    finally { setIsRejecting(false); }
  };

//   const handleSelectRequest = (requestId) => {
//     setSelectedRequests(prev => prev.includes(requestId) ? prev.filter(id => id !== requestId) : [...prev, requestId]);
//   };
// const handleSelectAll = () => {
//     const pageIds = paginatedRequests.map(r => r.id);
//     const allPageSelected = pageIds.every(id => selectedRequests.includes(id));
//     if (allPageSelected) {
//         setSelectedRequests(prev => prev.filter(id => !pageIds.includes(id)));
//     } else {
//         setSelectedRequests(prev => [...new Set([...prev, ...pageIds])]);
//     }
// };

// const handleBulkApprove = async () => {
//     if (!currentUser?.id || !selectedRequests.length) return;
//     setIsBulkApproving(true);
//     try {
//         const { data } = await api.put('/api/leaves/bulk-approve', 
//             { leaveIds: selectedRequests },
//             { params: { managerId: currentUser.id } }
//         );
//         // data = { succeeded, failed, errors }
//         if (data.succeeded > 0) toast.success(`${data.succeeded} request(s) approved`);
//         if (data.failed > 0) {
//             toast.error(`${data.failed} request(s) failed`);
//             // Optional: show per-item errors
//             data.errors.forEach(err => console.warn('[Bulk Approve]', err));
//         }
//         await fetchPendingCount();
//         await fetchLeaveRequests();
//         setSelectedRequests([]);
//     } catch (e) {
//         toast.error('Bulk approval request failed');
//     } finally {
//         setIsBulkApproving(false);
//     }
// };

// const handleBulkReject = async () => {
//     if (!bulkRejectComment.trim()) { toast.error('Please provide a reason'); return; }
//     setIsBulkRejecting(true);
//     try {
//         const { data } = await api.put('/api/leaves/bulk-reject',
//             { leaveIds: selectedRequests, comment: bulkRejectComment },
//             { params: { managerId: currentUser.id } }
//         );
//         if (data.succeeded > 0) toast.success(`${data.succeeded} request(s) rejected`);
//         if (data.failed > 0) {
//             toast.error(`${data.failed} request(s) failed`);
//             data.errors.forEach(err => console.warn('[Bulk Reject]', err));
//         }
//         await fetchPendingCount();
//         await fetchLeaveRequests();
//         setSelectedRequests([]);
//         setShowBulkRejectModal(false);
//         setBulkRejectComment('');
//     } catch (e) {
//         toast.error('Bulk rejection failed');
//     } finally {
//         setIsBulkRejecting(false);
//     }
// };


  useEffect(() => {
    if (!loading) {
      if (!currentUser) { navigate('/login'); return; }
      if (!['MANAGER', 'CEO'].includes(currentUser.role)) navigate('/unauthorized');
    }
  }, [currentUser, loading, navigate]);

  if (loading || !currentUser) {
    return (
      <div className="p-4 sm:p-6 w-full mx-auto">
        <div className="sm:hidden space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 animate-pulse">
              <div className="flex gap-3 mb-3">
                <div className="w-9 h-9 bg-slate-200 rounded-xl"></div>
                <div className="space-y-2 flex-1"><div className="h-4 w-28 bg-slate-200 rounded"></div><div className="h-3 w-20 bg-slate-200 rounded"></div></div>
                <div className="h-6 w-16 bg-slate-200 rounded-full"></div>
              </div>
              <div className="h-px bg-slate-100 my-2"></div>
              <div className="h-3 w-40 bg-slate-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const isActionInProgress = isApproving || isRejecting || isReapproving;
  // const isBulkActionInProgress = isBulkApproving || isBulkRejecting;

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <motion.div className="flex justify-between items-center mb-6 bg-slate-200 rounded-2xl p-6" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <div className='flex items-center gap-4 text-xl xs:text-2xl font-bold truncate'>
          <div className='p-2 rounded-xl bg-slate-700 text-slate-100'><HiOutlineClipboardList className="text-2xl" /></div>
          <h2 className="text-xl xs:text-2xl font-bold">{pageTitle}</h2>
        </div>
<div className="relative" ref={filterRef}>
  <button
    onClick={() => {
      if (!filterOpen) {
        setDraftManager(selectedManager);
        setDraftLocation(selectedLocation);
        setDraftType(leaveType);
        setDraftStatus(status);
      }
      setFilterOpen(prev => !prev);
    }}
    className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-medium transition
      ${activeFilterCount > 0
        ? 'bg-slate-700 text-white border-slate-700'
        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
      }`}
  >
    <IoFilterSharp className="text-base" />
    <span>Filter</span>
    {activeFilterCount > 0 && (
      <span className="ml-1 bg-white text-slate-700 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
        {activeFilterCount}
      </span>
    )}
  </button>

  {filterOpen && (
    <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-4 space-y-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-slate-700">Filters</span>
        {activeFilterCount > 0 && (
          <button
            onClick={() => {
              setDraftType('ALL'); setDraftStatus('ALL');
              setDraftManager(''); setDraftLocation('');
              setLeaveType('ALL'); setStatus('ALL');
              setSelectedManager(''); setSelectedLocation('');
            }}
            className="text-xs text-red-500 hover:text-red-600 font-medium"
          >Clear all</button>
        )}
      </div>

      {/* CEO-only filters */}
      {isCEO && (
        <>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Leave Type</label>
            <select value={draftType} onChange={(e) => setDraftType(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="ALL">All Types</option>
              <option value="Earned Leave">Earned Leave</option>
              <option value="Sick Leave">Sick Leave</option>
              <option value="Comp Off Leave">Comp Off Leave</option>
              <option value="Emergency Leave">Emergency Leave</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Manager</label>
            <select value={draftManager} onChange={(e) => setDraftManager(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="">All Managers</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </>
      )}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Status</label>
            <select value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              <option value="ALL">All Status</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
      {/* Location — visible to both CEO and Manager */}
      <div>


        <label className="block text-xs font-medium text-slate-500 mb-1.5">Location</label>
        <div className="flex gap-2">
          {['Bengaluru', 'Mangaluru'].map(loc => (
            <button key={loc}
              onClick={() => setDraftLocation(prev => prev === loc ? '' : loc)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition
                ${draftLocation === loc
                  ? 'bg-slate-700 text-white border-slate-700'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            >{loc}</button>
          ))}
        </div>
      </div>

      <button
onClick={() => {
  setStatus(draftStatus);

  if (isCEO) {
    setLeaveType(draftType);
    setSelectedManager(draftManager);
  }

  setSelectedLocation(draftLocation);
  setFilterOpen(false);
}}
        className="w-full bg-slate-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-600 transition"
      >Apply Filters</button>
    </div>
  )}
</div>
      </motion.div>

      {/* Bulk Action Bar */}
      {/* {selectedRequests.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex justify-between items-center">
          <span className="font-semibold text-blue-800">{selectedRequests.length} request(s) selected</span>
          <div className="flex gap-3">
            <button onClick={handleBulkApprove} disabled={isBulkActionInProgress} className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 ${isBulkActionInProgress ? 'opacity-60 cursor-not-allowed' : ''}`}>
              {isBulkApproving ? <><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Approving...</span></> : <><IoCheckmarkCircleOutline className="text-lg" /><span>Approve All</span></>}
            </button>
            <button onClick={() => setShowBulkRejectModal(true)} disabled={isBulkActionInProgress} className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 ${isBulkActionInProgress ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <IoCloseCircleOutline className="text-lg" /><span>Reject All</span>
            </button>
            <button onClick={() => setSelectedRequests([])} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">Clear</button>
          </div>
        </motion.div>
      )} */}

      {/* Bulk Reject Modal */}
      {/* {showBulkRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Reject Multiple Requests</h3>
              <button onClick={() => { setShowBulkRejectModal(false); setBulkRejectComment(''); }} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">You are about to reject <strong>{selectedRequests.length}</strong> request(s). Please provide a reason.</p>
              <textarea placeholder="Enter reason for rejection..." value={bulkRejectComment} onChange={(e) => setBulkRejectComment(e.target.value)} className="w-full p-3 border border-slate-200 rounded-lg text-sm" rows={4} autoFocus />
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowBulkRejectModal(false); setBulkRejectComment(''); }} className="px-4 py-2 text-sm font-medium text-slate-600" disabled={isBulkRejecting}>Cancel</button>
                <button onClick={handleBulkReject} disabled={isBulkRejecting || !bulkRejectComment.trim()} className={`px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 ${isBulkRejecting || !bulkRejectComment.trim() ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  {isBulkRejecting ? <><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Rejecting...</span></> : "Reject All"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )} */}

      {/* View Modal — unchanged */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => { if (!isActionInProgress && e.target === e.currentTarget) setShowViewModal(false); }} style={{ pointerEvents: isActionInProgress ? 'none' : 'auto' }}>
          <motion.div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Leave Request Details</h3>
              <button onClick={() => !isActionInProgress && setShowViewModal(false)} disabled={isActionInProgress} className={`text-slate-400 hover:text-slate-600 ${isActionInProgress ? 'cursor-not-allowed opacity-50' : ''}`}>✕</button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-500">Employee Name</p><p className="font-medium text-slate-800">{selectedRequest.employeeName}</p></div>
                <div><p className="text-sm text-slate-500">Leave Type</p><p className="font-medium text-blue-600">{selectedRequest.type}</p></div>
              </div>
              <div><p className="text-sm text-slate-500">Leave Duration</p><p className="font-medium text-slate-800">{selectedRequest.duration} {selectedRequest.duration === 1 || selectedRequest.duration === 0.5 ? "day" : "days"}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-500">Start Date</p><p className="font-medium text-slate-800">{formatDate(selectedRequest.startDate)} </p></div>
                <div><p className="text-sm text-slate-500">End Date</p><p className="font-medium text-slate-800">{formatDate(selectedRequest.endDate)} </p></div>
              </div>
              <div><p className="text-sm text-slate-500">Reason</p><p className="font-medium text-slate-800">{selectedRequest.reason}</p></div>
              <div className="grid grid-cols-2 gap-4">
  <div>
    <p className="text-sm text-slate-500">Applied On</p>
    <p className="font-medium text-slate-800">
      {selectedRequest.appliedOn ? new Date(selectedRequest.appliedOn).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
    </p>
  </div>
  <div>
    <p className="text-sm text-slate-500">
      {selectedRequest.status === 'APPROVED' ? 'Approved Date' : selectedRequest.status === 'REJECTED' ? 'Rejected Date' : 'Edited Date'}
    </p>
    <p className="font-medium text-slate-800">
      {selectedRequest.lastModifiedOn ? new Date(selectedRequest.lastModifiedOn).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
    </p>
  </div>
</div>
              <div className="flex flex-col gap-3 pt-4">
                {(selectedRequest.status === "PENDING" || selectedRequest.status === "APPROVED") && (
                  <><div className="flex items-center gap-2 text-sm text-slate-600"><span>Note:</span><span className="italic">Comment is required only for rejection</span></div>
                  <textarea placeholder="Enter reason for rejection..." value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} className="w-full p-3 border border-slate-200 rounded-lg text-sm" rows={3} /></>
                )}
                {selectedRequest.status === "REJECTED" && selectedRequest.approverComment && (
                  <div><p className="text-sm text-slate-500">Rejection Reason</p><p className="font-medium text-slate-800">{selectedRequest.approverComment}</p></div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                {selectedRequest.status === "PENDING" && (
                  <>
                    <button onClick={() => handleApprove(selectedRequest.id)} disabled={isApproving} className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 disabled:text-green-300 disabled:cursor-not-allowed flex items-center gap-2">
                      {isApproving ? <><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Approving...</span></> : "Approve"}
                    </button>
                    <button onClick={() => handleReject(selectedRequest.id)} disabled={isRejecting} className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 disabled:text-red-300 disabled:cursor-not-allowed flex items-center gap-2">
                      {isRejecting ? <><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Rejecting...</span></> : "Reject"}
                    </button>
                  </>
                )}
                {selectedRequest.status === "APPROVED" && (
                  <><button disabled className="px-4 py-2 text-sm font-medium text-slate-400 cursor-not-allowed">Approve</button>
                  <button onClick={() => handleReject(selectedRequest.id)} disabled={isRejecting} className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 disabled:text-red-300 disabled:cursor-not-allowed flex items-center gap-2">
                    {isRejecting ? <><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Rejecting...</span></> : "Reject"}
                  </button></>
                )}
                {selectedRequest.status === "REJECTED" && (
                  <><button onClick={() => handleApprove(selectedRequest.id, true)} disabled={isReapproving} className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 disabled:text-green-300 disabled:cursor-not-allowed flex items-center gap-2">
                    {isReapproving ? <><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Reapproving...</span></> : "Reapprove"}
                  </button>
                  <button disabled className="px-4 py-2 text-sm font-medium text-slate-400 cursor-not-allowed">Reject</button></>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Desktop View — unchanged */}
      <motion.div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                {/* <th className="px-6 py-4 font-semibold text-slate-600 text-left"><input type="checkbox" checked={paginatedRequests.length > 0 && paginatedRequests.every(req => selectedRequests.includes(req.id))} onChange={handleSelectAll} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" /></th> */}
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Sl. No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Employee ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Employee Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Start Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">End Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Type</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr><td colSpan="8" className="px-6 py-8 text-center"><div className="flex justify-center"><svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg></div></td></tr>
              ) : !apiLoading && paginatedRequests.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-8 text-center text-slate-500">No leave requests found.</td></tr>
              ) : null}
              {paginatedRequests.map((request, index) => (
                <tr key={request.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                  {/* <td className="px-6 py-4"><input type="checkbox" checked={selectedRequests.includes(request.id)} onChange={() => handleSelectRequest(request.id)} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" /></td> */}
                  <td className="px-6 py-4 text-sm text-slate-800 font-medium">{(currentPage - 1) * requestsPerPage + index + 1}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 font-medium">{request.employeeId}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 font-medium">{request.employeeName}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 font-medium">{formatDate(request.startDate)}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 font-medium">{formatDate(request.endDate)}</td>
                  <td className="px-6 py-4 text-sm text-slate-800 font-medium">{request.type}</td>
                  <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-medium ${request.status === "APPROVED" ? "bg-green-100 text-green-800" : request.status === "PENDING" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>{request.status}</span></td>
                  <td className="px-6 py-4"><div className="flex justify-center"><motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleView(request)} className="text-blue-600 hover:text-blue-800 transition-colors" title="View Details"><IoEyeOutline className="text-xl" /></motion.button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`${paginatedRequests.length > 0 ? 'block' : 'hidden'} px-6 py-4 border-t border-slate-100`}>
            <Stack spacing={2} className="flex justify-end">
              <Pagination count={totalPages} page={currentPage} onChange={(_, page) => setCurrentPage(page)} variant="outlined" shape="rounded-full" size="large" showFirstButton showLastButton sx={{ "& .MuiPaginationItem-root": { color: "#475569", borderColor: "#e2e8f0" }, "& .Mui-selected": { backgroundColor: "#475569 !important", color: "#fff" } }} />
            </Stack>
          </div>
        </div>
      </motion.div>

      {/* ─────────────────────────────────────────────────────────
          MOBILE VIEW — IMPROVED
      ───────────────────────────────────────────────────────── */}
      <div className="sm:hidden space-y-3">
        {apiLoading ? (
          [1,2,3].map(i => (
            <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 animate-pulse">
              <div className="flex gap-3 mb-3">
                <div className="w-9 h-9 bg-slate-200 rounded-xl"></div>
                <div className="space-y-2 flex-1"><div className="h-4 w-28 bg-slate-200 rounded"></div><div className="h-3 w-20 bg-slate-200 rounded"></div></div>
                <div className="h-6 w-16 bg-slate-200 rounded-full"></div>
              </div>
              <div className="h-px bg-slate-100 my-2"></div>
              <div className="h-3 w-40 bg-slate-200 rounded"></div>
            </div>
          ))
        ) : paginatedRequests.length > 0 ? (
          paginatedRequests.map((request, index) => {
            const status = statusConfig[request.status] || statusConfig["PENDING"];
            const abbr = leaveTypeAbbr[request.type] || "?";
            const typeColor = leaveTypeColor[request.type] || "bg-slate-100 text-slate-600";
            const initials = getInitials(request.employeeName);
            const aColor = avatarColor(request.employeeName);

            return (
              <motion.div
                key={request.id}
                onClick={() => handleView(request)}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.04 }}
              >


                <div className="p-4">
                  {/* Row 1: checkbox + employee avatar + name + status + view */}
                  <div className="flex items-start gap-2">
                    {/* Checkbox */}
                    {/* <div onClick={e => e.stopPropagation()} className="pt-0.5">
                      <input type="checkbox" checked={selectedRequests.includes(request.id)} onChange={() => handleSelectRequest(request.id)} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                    </div> */}

                    {/* Employee avatar */}
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-bold shrink-0 ${aColor}`}>{initials}</div>

                    {/* Name + leave type */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-800 leading-tight truncate">{request.employeeName}</p>
                      <span className={`inline-block mt-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${typeColor}`}>{request.type}</span>
                    </div>

                    {/* Status pill */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border shrink-0 ${status.bg} ${status.text} ${status.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                      {status.label}
                    </span>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-100 my-3" />

                  {/* Row 2: date range + duration */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
                      <BsCalendar4 className="text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-700">{formatDate(request.startDate)}</span>
                      {request.startDate !== request.endDate && (
                        <><span className="text-slate-300 mx-0.5">→</span><span className="font-medium text-slate-700">{formatDate(request.endDate)}</span></>
                      )}
                    </div>
                    {request.duration && (
                      <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                        {request.duration} {request.duration === 0.5 ? "½ day" : request.duration === 1 ? "day" : "days"}
                      </span>
                    )}
                  </div>

                  {/* Row 3: reason */}
                  {request.reason && (
                    <p className="mt-2.5 text-[12px] text-slate-400 line-clamp-1">
                      <span className="font-medium text-slate-500">Reason: </span>{request.reason}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
            <HiOutlineClipboardList className="text-4xl text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-400">No leave requests found.</p>
          </div>
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-[12px] text-slate-400 font-medium">Page <span className="text-slate-600 font-semibold">{currentPage}</span> of {totalPages}</span>
            <div className="flex items-center gap-1.5">
              <motion.button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage===1} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage===1 ? "border-slate-100 text-slate-300 cursor-not-allowed" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} whileTap={{ scale: 0.92 }}>‹</motion.button>
              {Array.from({ length: totalPages }, (_, i) => i+1).filter(p => totalPages <= 5 || p===1 || p===totalPages || Math.abs(p-currentPage)<=1).reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx-1] > 1) acc.push('...'); acc.push(p); return acc; }, []).map((item, idx) =>
                item === '...' ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-slate-300 text-xs">…</span>
                : <motion.button key={item} onClick={() => setCurrentPage(item)} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-[12px] font-semibold ${currentPage===item ? "bg-slate-800 border-slate-800 text-white shadow-sm" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`} whileTap={{ scale: 0.92 }}>{item}</motion.button>
              )}
              <motion.button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage===totalPages} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage===totalPages ? "border-slate-100 text-slate-300 cursor-not-allowed" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} whileTap={{ scale: 0.92 }}>›</motion.button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManagerLeaveRequests;